import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Card,
    CircularProgress,
    ClickAwayListener,
    IconButton,
    Popper,
    Tooltip,
} from '@mui/material';
import { Bell, BellOff, Check } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SC_NAVBAR_IN_APP_NOTIFICATION } from '../../../lib/queryKeys';
import useMeService from '../../../services/MeService';
import useInAppNotificationsService from '../../../services/InAppNotificationsService';
import { useSSE } from '../../SSEProvider';

type Notification = {
    id: number;
    message: string;
};

type NotificationsResponse = {
    data: Notification[];
    metadata: {
        total: number;
    };
};

/**
 * Campana de notificaciones de la cabecera del sidebar. Mantiene el debounce de
 * las invalidaciones por SSE y la actualización optimista al marcar como leída
 * que ya tenía la AppBar.
 */
export default function SidebarNotifications(): React.ReactElement {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const meService = useMeService();
    const inAppNotificationsService = useInAppNotificationsService();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const notificationsQuery = useQuery<NotificationsResponse>({
        queryKey: [SC_NAVBAR_IN_APP_NOTIFICATION],
        queryFn: () =>
            meService.getMeNotifications() as Promise<NotificationsResponse>,
        // Mantener datos frescos 30s: evita refetches automáticos por
        // re-mounts del sidebar. invalidateQueries() de SSE/markAsRead
        // sigue forzando refetch al instante (no respeta staleTime).
        staleTime: 30_000,
        gcTime: 5 * 60_000,
    });

    // Debounce de las invalidaciones por evento SSE. Si llegan 10 eventos
    // inAppNotification:new en 500ms (típico cuando el backend hace broadcast a
    // muchos usuarios o el cliente abre varias tabs), se ejecuta UNA sola
    // invalidación al final del debounce en lugar de 10.
    const invalidateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const handleNotificationSseEvent = useCallback(() => {
        if (invalidateDebounceRef.current !== null) {
            clearTimeout(invalidateDebounceRef.current);
        }
        invalidateDebounceRef.current = setTimeout(() => {
            invalidateDebounceRef.current = null;
            void queryClient.invalidateQueries({
                queryKey: [SC_NAVBAR_IN_APP_NOTIFICATION],
            });
        }, 500);
    }, [queryClient]);
    useEffect(
        () => () => {
            if (invalidateDebounceRef.current !== null) {
                clearTimeout(invalidateDebounceRef.current);
            }
        },
        [],
    );
    useSSE('inAppNotification:new', handleNotificationSseEvent);

    const notifications = notificationsQuery.data?.data ?? [];
    const notificationCount = notificationsQuery.data?.metadata.total ?? 0;

    const handleMarkAsRead = async (notificationId: number): Promise<void> => {
        // Cancelar refetches en vuelo para que no sobrescriban la actualización
        // optimista.
        await queryClient.cancelQueries({
            queryKey: [SC_NAVBAR_IN_APP_NOTIFICATION],
        });

        const previousNotifications = queryClient.getQueryData([
            SC_NAVBAR_IN_APP_NOTIFICATION,
        ]);

        queryClient.setQueryData(
            [SC_NAVBAR_IN_APP_NOTIFICATION],
            (oldData: NotificationsResponse | undefined) => {
                if (!oldData) {
                    return { data: [], metadata: { total: 0 } };
                }
                return {
                    ...oldData,
                    data: oldData.data.filter(
                        (notification) => notification.id !== notificationId,
                    ),
                    metadata: {
                        ...oldData.metadata,
                        total:
                            oldData.metadata.total > 0
                                ? oldData.metadata.total - 1
                                : 0,
                    },
                };
            },
        );

        try {
            await inAppNotificationsService.markAsRead(notificationId);
            // OK: la actualización optimista YA refleja el estado correcto. No
            // refetcheamos para evitar peticiones innecesarias — el SSE
            // (debounced) sincronizará si hay cambios externos.
        } catch (error) {
            // Rollback y refetch para corregir el estado tras el fallo. La
            // invalidación SOLO ocurre en error.
            queryClient.setQueryData(
                [SC_NAVBAR_IN_APP_NOTIFICATION],
                previousNotifications,
            );
            await queryClient.invalidateQueries({
                queryKey: [SC_NAVBAR_IN_APP_NOTIFICATION],
            });
            throw error;
        }
    };

    return (
        <>
            {/* Campana y desplegable calcados del NotificationsBell de
                sporttia-competitions: contador propio en la esquina, tarjeta de
                320px a la derecha, lista separada por líneas y un botón
                fantasma por notificación para marcarla como leída. */}
            <Tooltip title={t('configuration.notifications')} arrow>
                <button
                    type="button"
                    id="notifications"
                    aria-label={t('configuration.notifications')}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                    className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-md transition-colors"
                >
                    <Bell className="h-4 w-4" />
                    {notificationCount > 0 && (
                        <span className="bg-destructive text-destructive-foreground absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold">
                            {notificationCount > 99 ? '99+' : notificationCount}
                        </span>
                    )}
                </button>
            </Tooltip>

            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="right-start"
                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                sx={{ zIndex: 1300 }}
            >
                <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                    <Card className="w-80 max-w-[90vw] overflow-hidden p-0">
                        <div className="border-border flex items-center justify-between border-b px-4 py-3">
                            <span className="text-sm font-semibold">
                                {t('configuration.notifications')}
                            </span>
                            {notificationCount > 0 && (
                                <span className="text-muted-foreground text-xs tabular-nums">
                                    {notificationCount}
                                </span>
                            )}
                        </div>

                        <div className="max-h-96 overflow-y-auto">
                            {notificationsQuery.isLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <CircularProgress size={20} />
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="text-muted-foreground flex flex-col items-center gap-2 py-10 text-center text-sm">
                                    <BellOff className="h-5 w-5" />
                                    {t('notifications.noNewNotifications')}
                                </div>
                            ) : (
                                <ul className="divide-border m-0 list-none divide-y p-0">
                                    {notifications.map((notification) => (
                                        <li
                                            key={notification.id}
                                            className="flex items-start gap-2 px-4 py-3"
                                        >
                                            <p className="text-muted-foreground m-0 min-w-0 flex-1 text-sm break-words">
                                                {notification.message}
                                            </p>
                                            <Tooltip
                                                title={t('labels.read')}
                                                arrow
                                            >
                                                <IconButton
                                                    size="small"
                                                    aria-label={t(
                                                        'labels.read',
                                                    )}
                                                    className="text-muted-foreground hover:text-foreground h-7 w-7 shrink-0"
                                                    onClick={() =>
                                                        handleMarkAsRead(
                                                            notification.id,
                                                        )
                                                    }
                                                >
                                                    <Check className="h-4 w-4" />
                                                </IconButton>
                                            </Tooltip>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </Card>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
